import React, { useState, useEffect, Fragment } from "react";
import { IconButton, Input, Option, Select, Spinner, Tooltip, Typography } from "@material-tailwind/react";
import { Edit2Icon, PhoneCallIcon, MagnetIcon, TrashIcon, XIcon, EyeIcon, MinusIcon, ArrowUpIcon, ArrowDownIcon } from "lucide-react";
import { DeleteLead } from "../../services/backend";
import moment from "moment";
import { convertToPresentableText, Notify, NotifyError } from "../../utils/functions";
import {
	DEAL_STAGES,
	DEAL_STAGES_ENUM,
	MARKETING_CHANNELS_ENUM
} from "../../utils/enums";

// Import components
import LeadFormModal from "./leadFormModal";
import EngagementFormModal from "./engagementFormModal";
import { useGetLeads } from "../../custom-hooks/use-leads";
import Rating from "../../components/rating";
import { InterpretLeadStatus } from "../../utils/ui-interpreters";
import Pagination from "../../components/pagination";
import { PageHeader } from "../../components";

const LeadManagementPage = () => {
	// State variables
	const [formModalOpen, setFormModalOpen] = useState(false);
	const [engagementModalOpen, setEngagementModalOpen] = useState(false);
	const [selectedLeadId, setSelectedLeadId] = useState(null);
	const [isUpdating, setIsUpdating] = useState(false);
	const [isReadOnly, setIsReadOnly] = useState(false);
	const [refreshRequired, setRefreshRequired] = useState(0);

	// Filters
	const [filterStatus, setFilterStatus] = useState('');
	const [filterSource, setFilterSource] = useState('');
	const [searchTerm, setSearchTerm] = useState('');

	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState(10);
	const [sort, setSort] = useState('-createdDate');

	const [debouncedSearch, setDebouncedSearch] = useState('');
	const [isTyping, setIsTyping] = useState(false);
	const [filters, setFilters] = useState({});

	const {
		data: list,
		isLoading: isLeadLoading,
		// error: leadsError, 
		refetch
	} = useGetLeads({
		_page: page,
		_limit: limit,
		_search: debouncedSearch,
		_sort: sort,
		...filters
	});

	const {
		total,
		totalPages
	} = list || {};

	// Debounce the search input to avoid making API calls on every keystroke
	useEffect(() => {
		setIsTyping(true);
		const timer = setTimeout(() => {
			setDebouncedSearch(searchTerm);
			setIsTyping(false);
		}, 500); // Wait for 500ms after the customer stops typing

		return () => {
			clearTimeout(timer);
		};
	}, [searchTerm]);

	// Reset to page 1 when search changes
	useEffect(() => {
		setPage(1);
	}, [debouncedSearch, filters]);

	useEffect(() => {
		const fetchLeads = async () => {
			try {
				// if (refreshRequired > 1) {
				await refetch();
				// }
			} catch (error) {
				console.error('Error fetching leads:', error);
				NotifyError('Failed to fetch leads', error);
			}
		};

		fetchLeads();
	}, [refreshRequired, refetch]);

	// Open modal for adding a new lead
	const openAddLeadModal = () => {
		setFormModalOpen(true);
		setIsUpdating(false);
		setSelectedLeadId(null);
		setIsReadOnly(false);
	};

	// Open modal for updating a lead
	const openUpdateLeadModal = (leadId, readOnly) => {
		setFormModalOpen(true);
		setIsUpdating(true);
		setSelectedLeadId(leadId);
		setIsReadOnly(readOnly);
	};

	// Open modal for adding engagement
	const openAddEngagementModal = (leadId) => {
		setSelectedLeadId(leadId);
		setEngagementModalOpen(true);
	};

	// Delete lead
	const handleDeleteLead = async (leadId) => {
		if (window.confirm('Are you sure you want to delete this lead?')) {
			try {
				await DeleteLead(leadId);
				setRefreshRequired(refreshRequired + 1);
				Notify('Lead deleted successfully', 'success');
			} catch (error) {
				console.error('Error deleting lead', error);
				NotifyError('Failed to delete lead', error);
			}
		}
	};

	// Handle filtering
	const handleFilterChange = (filter, value) => {
		if (filter === 'status') {
			setFilterStatus(value);
			setFilters({ ...filters, leadStatus: value });
		} else if (filter === 'source') {
			setFilterSource(value);
			setFilters({ ...filters, leadSource: value });
		}
	};

	const handleSearchChange = (e) => {
		setSearchTerm(e.target.value);
	};


	const TABLE_COLUMNS = [
		{
			label: 'Name',
			key: 'name',
			sortable: true,
			sort: sort === "fullName" ? "-fullName" : "fullName"
		},
		{
			label: 'Status',
			key: 'leadStatus',
			sortable: true,
			sort: sort === "leadStatus" ? "-leadStatus" : "leadStatus"
		},
		{
			label: 'Phone',
			key: 'phoneNumber',
			sortable: true,
			sort: sort === "phoneNumber" ? "-phoneNumber" : "phoneNumber"
		},
		{
			label: 'Source',
			key: 'leadSource',
			sortable: true,
			sort: sort === "leadSource" ? "-leadSource" : "leadSource"
		},
		{
			label: 'Requirements',
			key: 'notes',
			sortable: false,
			sort: null
		},
		{
			label: 'Connection',
			key: 'connectionDate',
			sortable: true,
			sort: sort === "connectionDate" ? "-connectionDate" : "connectionDate"
		},
		{
			label: 'Actions',
			key: 'actions',
			sortable: false,
			sort: null
		}
	];

	return (
		<div>
			<div className="m-5 p-5 border rounded-3xl">
				<PageHeader
					title="Lead Management"
					isLoading={isLeadLoading}
					actionOnClick={openAddLeadModal}
					actionType="add"
					actionText="Add New Lead"
				/>

				{/* Filters */}
				<div className="grid grid-cols-1 lg:grid-cols-3 gap-4 my-4">
					<Input
						label="Search Leads"
						value={searchTerm}
						onChange={handleSearchChange}
						icon={isTyping ? <Spinner className="h-4 w-4" /> : <XIcon className={searchTerm ? "cursor-pointer" : "hidden"} size={16} onClick={() => setSearchTerm('')} />}
					/>
					<Select
						label="Filter by Status"
						value={filterStatus}
						onChange={(value) => handleFilterChange('status', value)}
						selected={(elem) =>
							elem ? (
								<div className="flex items-center gap-2">
									<div>
										<span>{convertToPresentableText(filterStatus)}</span>
									</div>
									<div>
										<XIcon className="cursor-pointer" size={12} onClick={(e) => {
											e.stopPropagation();
											handleFilterChange('status', '');
										}} />
									</div>
								</div>
							) : null
						}
					>
						{DEAL_STAGES_ENUM.map((status, index) => (
							<Option key={index} value={status}>
								{convertToPresentableText(status)}
							</Option>
						))}
					</Select>
					<Select
						label="Filter by Source"
						value={filterSource}
						onChange={(value) => handleFilterChange('source', value)}
						selected={(elem) =>
							elem ? (
								<div className="flex items-center gap-2">
									<div>
										<span>{convertToPresentableText(filterSource)}</span>
									</div>
									<div>
										<XIcon className="cursor-pointer" size={12} onClick={(e) => {
											e.stopPropagation();
											handleFilterChange('source', '');
										}} />
									</div>
								</div>
							) : null
						}
					>
						{MARKETING_CHANNELS_ENUM.map((source, index) => (
							<Option key={index} value={source}>
								{convertToPresentableText(source)}
							</Option>
						))}
					</Select>
				</div>

				{isLeadLoading ? (
					<div className="flex justify-center">
						<Spinner />
					</div>
				) : (
					<div className="overflow-x-auto">
						<table className="w-full min-w-max table-auto text-left">
							<thead className="text-sm">
								<tr className="py-3">
									<th colSpan={7} className="py-3 text-xs font-light text-orange-900">
										Showing <span className="font-bold">{limit < total ? limit : total}</span> Business Leads of <span className="font-bold">{total}</span>
									</th>
								</tr>
								<tr className="py-3 uppercase">
									{
										TABLE_COLUMNS.map((column, index) => (
											<th
												key={index}
												className={"cursor-pointer py-3"}
												onClick={() => setSort(column.sortable ? column.sort : sort)}
											>
												<Typography
													variant="small"
													color="blue-gray"
													className="flex items-center gap-2 font-normal leading-none opacity-70">
													{column.label} {column.sortable ? (column.sort.startsWith('-') ? <ArrowUpIcon size={12} /> : <ArrowDownIcon size={12} />) : ''}
												</Typography>
											</th>
										))
									}
								</tr>
							</thead>
							<tbody>
								{list?.leads?.length === 0 ? (
									<tr>
										<td colSpan={7} className="py-6 text-center text-gray-500">
											No leads found matching your filters.
										</td>
									</tr>
								) : (
									list?.leads?.map((lead, index) => (
										<tr key={index} className="border-t hover:bg-gray-50 button rounded-full">
											<td className="font-thin py-3 text-sm">
												<div>
													<div className="font-bold">{lead.fullName}</div>
													<div className="flex items-center gap-1 font-bold">
														<Rating rating={lead.score / 20} /> <MinusIcon size={12} /> {lead.score} <div className="text-xs font-thin text-gray-900">{lead.companyName}</div>
													</div>
												</div>
											</td>
											<td>
												<div className="flex">
													{
														lead.leadStatus === DEAL_STAGES.CONTACTED && lead.engagementHistory?.length > 1 ? <Fragment>
															{InterpretLeadStatus(lead.leadStatus, "chip", `${lead.engagementHistory?.length || 0} Follow-ups`)}
														</Fragment> :
															InterpretLeadStatus(lead.leadStatus)
													}
												</div>
											</td>
											{/* <td >
												<div className="flex">
													{InterpretEngagementLevel(lead.engagementLevel)}
												</div>
											</td> */}
											<td className="font-thin py-3 text-sm">
												{lead.phoneNumber ? (
													<a href={`tel:${lead.countryCode}-${lead.phoneNumber}`} className="hover:underline text-blue-700 font-mono tabular-nums">
														{lead.countryCode} {lead.phoneNumber}
													</a>
												) : (
													<span className="text-gray-500">Not added</span>
												)}
											</td>
											<td className="font-thin py-3 text-sm">
												{convertToPresentableText(lead.leadSource)}
											</td>
											<td className="font-thin py-3 px-2 text-xs whitespace-pre-wrap">
												{lead.notes}
											</td>
											<td className="font-thin  text-xs">
												<Tooltip className="border border-blue-gray-50 bg-white px-4 py-3 shadow-xl shadow-black/10 text-black" content={
													<div className="flex flex-col gap-1">
														<div>
															Generated <strong>{moment(lead.connectionDate).format('lll')}</strong>
														</div>
														<div>
															Last contacted <strong>{lead.lastContactDate ? moment(lead.lastContactDate).format('lll') : 'Never'}</strong>
														</div>
													</div>
												} placement="bottom">
													<div>
														<div>
															{lead.connectionDate ? moment(lead.connectionDate).format('lll') : 'Never'}
														</div>
														<div className="flex items-center gap-1">
															<PhoneCallIcon color="blue" size={12} /> {lead.lastContactDate ? moment(lead.lastContactDate).format('lll') : 'Never'}
														</div>
													</div>
												</Tooltip>
											</td>
											<td className="flex gap-1 py-3">
												<Tooltip content="View Lead Status" placement="left">
													<IconButton
														size="sm"
														className="rounded-full"
														color="blue"
														onClick={() => openUpdateLeadModal(lead._id, true)}
													>
														<EyeIcon size={12} />
													</IconButton>
												</Tooltip>
												<Tooltip content="Update Lead" placement="bottom">
													<IconButton
														size="sm"
														color="orange"
														className="rounded-full"
														disabled={lead.leadStatus === DEAL_STAGES.WON}
														onClick={() => openUpdateLeadModal(lead._id, false)}
													>
														<Edit2Icon size={12} />
													</IconButton>
												</Tooltip>
												<Tooltip content="Add Follow-up" placement="right">
													<IconButton
														size="sm"
														className="rounded-full"
														color="green"
														disabled={lead.leadStatus === DEAL_STAGES.WON}
														onClick={() => openAddEngagementModal(lead._id)}
													>
														<MagnetIcon size={12} />
													</IconButton>
												</Tooltip>
												<IconButton
													size="sm"
													className="rounded-full hidden"
													color="red"
													onClick={() => handleDeleteLead(lead._id)}
												>
													<TrashIcon size={12} />
												</IconButton>
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
						{
							totalPages > 1 &&
							<Pagination
								page={page}
								totalPages={totalPages}
								limit={limit}
								onChangePage={setPage}
								onChangeLimit={setLimit}
							/>
						}
					</div>
				)}

				{/* Lead Form Modal (Add/Edit) */}
				<LeadFormModal
					isOpen={formModalOpen}
					onClose={() => setFormModalOpen(false)}
					leadId={selectedLeadId}
					isUpdating={isUpdating}
					onSuccess={() => setRefreshRequired(refreshRequired + 1)}
					readOnly={isReadOnly}
				/>

				{/* Engagement Form Modal */}
				<EngagementFormModal
					isOpen={engagementModalOpen}
					onClose={() => setEngagementModalOpen(false)}
					leadId={selectedLeadId}
					onSuccess={() => setRefreshRequired(refreshRequired + 1)}
				/>
			</div>
		</div>
	);
};

export default LeadManagementPage;